import { useEffect, useState } from "react";
import {
  getAllPromoters, getSalesByMonth, getAllTargets,
  getPendingSales, approveSale, rejectSale,
  getBanners, saveBanner, deleteBanner,
} from "../../firebase/services";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase/config";
import { calcSalesBonus } from "../../firebase/schema";
import { format } from "date-fns";
import { useLanguage } from "../../hooks/useLanguage";
import BannerSlider from "../../components/BannerSlider";

const MONTH = format(new Date(), "yyyy-MM");

export default function AdminDashboard() {
  const [promoters, setPromoters] = useState([]);
  const [salesMap, setSalesMap] = useState({});
  const [targetMap, setTargetMap] = useState({});
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banners, setBanners] = useState([]);
  const [bannerSlots, setBannerSlots] = useState([
    { title: "", imageUrl: "", linkUrl: "" },
    { title: "", imageUrl: "", linkUrl: "" },
    { title: "", imageUrl: "", linkUrl: "" },
  ]);
  const [bannerFiles, setBannerFiles] = useState([null, null, null]);
  const [bannerSaving, setBannerSaving] = useState(false);
  const [bannerMsg, setBannerMsg] = useState("");

  useEffect(() => {
    const load = async () => {
      const [proms, sales, targets, pend, bans] = await Promise.all([
        getAllPromoters().catch(() => []),
        getSalesByMonth(MONTH).catch(() => []),
        getAllTargets(MONTH).catch(() => []),
        getPendingSales().catch(() => []),
        getBanners().catch(() => []),
      ]);
      setPromoters(proms);
      const sm = {};
      sales.forEach(s => { sm[s.promoterId] = (sm[s.promoterId] || 0) + 1; });
      setSalesMap(sm);
      const tm = {};
      targets.forEach(t => { tm[t.promoterId] = t; });
      setTargetMap(tm);
      setPending(pend);
      setBanners(bans);
      const slots = [0, 1, 2].map(i => {
        const b = bans.find(b => b.order === i);
        return b ? { title: b.title || "", imageUrl: b.imageUrl || "", linkUrl: b.linkUrl || "" } : { title: "", imageUrl: "", linkUrl: "" };
      });
      setBannerSlots(slots);
      setLoading(false);
    };
    load();
  }, []);

  const handleApprove = async (id) => {
    await approveSale(id);
    setPending(p => p.filter(s => s.id !== id));
  };
  const handleReject = async (id) => {
    await rejectSale(id);
    setPending(p => p.filter(s => s.id !== id));
  };

  const handleSaveBanners = async () => {
    setBannerSaving(true);
    setBannerMsg("");
    try {
      for (let i = 0; i < 3; i++) {
        const slot = bannerSlots[i];
        const file = bannerFiles[i];
        let imageUrl = slot.imageUrl;
        if (file) {
          const r = ref(storage, `banners/slot${i}.jpg`);
          await uploadBytes(r, file);
          imageUrl = await getDownloadURL(r);
        }
        if (imageUrl || slot.title) {
          await saveBanner(`slot${i}`, { order: i, title: slot.title, imageUrl, linkUrl: slot.linkUrl });
        }
      }
      const bans = await getBanners();
      setBanners(bans);
      setBannerFiles([null, null, null]);
      setBannerMsg("✅ Đã lưu banner");
    } catch (err) {
      setBannerMsg("Lỗi: " + err.message);
    } finally {
      setBannerSaving(false);
      setTimeout(() => setBannerMsg(""), 3000);
    }
  };

  const handleClearBanner = async (i) => {
    await deleteBanner(`slot${i}`);
    setBannerSlots(s => s.map((b, idx) => idx === i ? { title: "", imageUrl: "", linkUrl: "" } : b));
    const bans = await getBanners();
    setBanners(bans);
  };

  const totalSold = Object.values(salesMap).reduce((a, b) => a + b, 0);
  const totalTarget = Object.values(targetMap).reduce((a, t) => a + (t.sellTarget || 0), 0);
  const avgHitRate = totalTarget > 0 ? ((totalSold / totalTarget) * 100).toFixed(1) : "—";

  const { t } = useLanguage();
  if (loading) return <div style={{ color: "#888", padding: 40 }}>{t("common.loading")}</div>;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p>{t("dash.overview")} {MONTH.slice(5)}/{MONTH.slice(0, 4)}</p>
        </div>
      </div>

      <BannerSlider banners={banners} />

      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-label">{t("dash.total_promoters")}</div>
          <div className="metric-value">{promoters.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">{t("dash.total_sales")}</div>
          <div className="metric-value">{totalSold}</div>
          <div className="metric-sub">{t("dash.target_lb")}: {totalTarget} {t("home.units")}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">{t("dash.avg_hit")}</div>
          <div className="metric-value">{avgHitRate}%</div>
          <div className="metric-sub">{t("dash.need80")}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">{t("dash.pending_orders")}</div>
          <div className="metric-value">{pending.length}</div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <div className="card-title">{t("dash.progress_title")}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {promoters.map(p => {
              const sold = salesMap[p.uid] || 0;
              const tgt = targetMap[p.uid]?.sellTarget || 0;
              const pct = tgt > 0 ? Math.min(100, Math.round((sold / tgt) * 100)) : 0;
              const color = pct >= 120 ? "#534AB7" : pct >= 100 ? "#C8152A" : pct >= 80 ? "#1D9E75" : pct >= 60 ? "#EF9F27" : "#E24B4A";
              return (
                <div key={p.uid}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                    <span>{p.name}</span>
                    <span style={{ color: "#888" }}>{sold}/{tgt} — {pct}%</span>
                  </div>
                  <div className="progress-wrap">
                    <div className="progress-bar" style={{ width: pct + "%", background: color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title">{t("dash.pending_title")}</div>
            {pending.length > 0 && <span className="badge badge-amber">{pending.length}</span>}
          </div>
          {pending.length === 0 ? (
            <div style={{ color: "#aaa", fontSize: 13, textAlign: "center", padding: "20px 0" }}>
              {t("sales.no_pending")}
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Promoter</th><th>Model</th><th>Serial</th><th></th>
                </tr>
              </thead>
              <tbody>
                {pending.slice(0, 8).map(s => (
                  <tr key={s.id}>
                    <td>{s.promoterName}</td>
                    <td style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.model}</td>
                    <td style={{ fontFamily: "monospace", fontSize: 12 }}>{s.serial}</td>
                    <td>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button className="btn btn-primary btn-sm" onClick={() => handleApprove(s.id)}>{t("common.approve_short")}</button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleReject(s.id)}>✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Banner Management */}
      <div className="card" style={{ marginTop: 8 }}>
        <div className="card-header">
          <div>
            <div className="card-title">🖼 {t("banner.manage_title")}</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{t("dash.banner_size")}</div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={handleSaveBanners} disabled={bannerSaving}>
            {bannerSaving ? t("common.saving") : t("banner.save")}
          </button>
        </div>
        {bannerMsg && <div className={`alert ${bannerMsg.startsWith("✅") ? "alert-success" : "alert-error"}`}>{bannerMsg}</div>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 8 }}>{t("banner.slot")} {i + 1}</div>
              {bannerSlots[i].imageUrl && (
                <img src={bannerSlots[i].imageUrl} alt="" style={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 6, marginBottom: 8 }} />
              )}
              <div className="form-group" style={{ marginBottom: 6 }}>
                <input
                  type="file" accept="image/*"
                  onChange={e => setBannerFiles(f => { const n = [...f]; n[i] = e.target.files[0]; return n; })}
                  style={{ fontSize: 12, border: "none", padding: 0 }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 6 }}>
                <input
                  placeholder={t("dash.banner_title_ph")}
                  value={bannerSlots[i].title}
                  onChange={e => setBannerSlots(s => s.map((b, idx) => idx === i ? { ...b, title: e.target.value } : b))}
                  style={{ fontSize: 12, padding: "5px 8px" }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 8 }}>
                <input
                  placeholder={t("banner.link_url")}
                  value={bannerSlots[i].linkUrl}
                  onChange={e => setBannerSlots(s => s.map((b, idx) => idx === i ? { ...b, linkUrl: e.target.value } : b))}
                  style={{ fontSize: 12, padding: "5px 8px" }}
                />
              </div>
              {(bannerSlots[i].imageUrl || bannerSlots[i].title) && (
                <button className="btn btn-sm" style={{ fontSize: 12 }} onClick={() => handleClearBanner(i)}>🗑 {t("banner.clear")}</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
